import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DocumentsTab = ({ documents, onUpload, onApprove, onReject }) => {
  const { t, currentLanguage } = useLanguage();
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const documentTypes = [
    { id: 'passport', label: t('pilgrimProfile.passportCopy'), icon: 'FileText' },
    { id: 'photo', label: t('pilgrimProfile.photo'), icon: 'Image' },
    { id: 'medical', label: t('pilgrimProfile.medicalCertificate'), icon: 'Heart' },
    { id: 'visa', label: t('pilgrimProfile.visaDocument'), icon: 'FileCheck' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-warning/10 text-warning', label: t('pilgrimProfile.pending') },
      approved: { color: 'bg-success/10 text-success', label: t('pilgrimProfile.approved') },
      rejected: { color: 'bg-error/10 text-error', label: t('pilgrimProfile.rejected') },
      not_uploaded: { color: 'bg-muted text-muted-foreground', label: t('pilgrimProfile.notUploaded') }
    };

    const config = statusConfig?.[status] || statusConfig?.not_uploaded;
    return (
      <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const handlePreview = (doc) => {
    setSelectedDocument(doc);
    setPreviewOpen(true);
  };

  const handleFileUpload = (docType, event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      onUpload(docType, file);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <h3 className="text-lg md:text-xl font-semibold text-foreground">
        {t('pilgrimProfile.documents')}
      </h3>
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {documentTypes?.map((docType) => {
          const doc = documents?.find(d => d?.type === docType?.id);
          
          return (
            <div
              key={docType?.id}
              className="bg-card rounded-lg border border-border p-4 md:p-6"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3 md:gap-4 flex-1">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name={docType?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base md:text-lg font-medium text-foreground mb-2">
                      {docType?.label}
                    </h4>
                    {doc ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(doc?.status)}
                        </div>
                        <p className="caption text-muted-foreground">
                          {doc?.status === 'approved' && `${t('pilgrimProfile.approvedOn')} ${new Date(doc.approvedDate)?.toLocaleDateString('en-US')}`}
                          {doc?.status === 'rejected' && `${t('pilgrimProfile.rejectedOn')} ${new Date(doc.rejectedDate)?.toLocaleDateString('en-US')}`}
                          {doc?.status === 'pending' && `${t('pilgrimProfile.uploadedOn')} ${new Date(doc.uploadDate)?.toLocaleDateString('en-US')}`}
                        </p>
                      </div>
                    ) : (
                      <p className="caption text-muted-foreground">
                        {t('pilgrimProfile.notUploaded')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 md:gap-3">
                  {doc ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Eye"
                        iconPosition="left"
                        onClick={() => handlePreview(doc)}
                      >
                        {t('pilgrimProfile.view')}
                      </Button>
                      {doc?.status === 'pending' && (
                        <>
                          <Button
                            variant="success"
                            size="sm"
                            iconName="Check"
                            iconPosition="left"
                            onClick={() => onApprove(doc?.id)}
                          >
                            {t('pilgrimProfile.approve')}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            iconName="X"
                            iconPosition="left"
                            onClick={() => onReject(doc?.id)}
                          >
                            {t('pilgrimProfile.reject')}
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <label>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileUpload(docType?.id, e)}
                      />
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Upload"
                        iconPosition="left"
                        asChild
                      >
                        <span>{t('pilgrimProfile.upload')}</span>
                      </Button>
                    </label>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {previewOpen && selectedDocument && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-warm-lg">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
              <h3 className="text-lg md:text-xl font-semibold text-foreground">
                {documentTypes?.find(dt => dt?.id === selectedDocument?.type)?.label}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={() => setPreviewOpen(false)}
              />
            </div>
            <div className="p-4 md:p-6 overflow-auto max-h-[calc(90vh-120px)]">
              <Image
                src={selectedDocument?.url}
                alt={`${documentTypes?.find(dt => dt?.id === selectedDocument?.type)?.label} - Document preview showing uploaded file for pilgrim verification`}
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;